import * as React from 'react';

import { cn } from '../../lib/utils';

import { Popover, PopoverAnchor, PopoverContent } from './popover';
import { SearchInput } from './search-input';
import type { SearchInputProps } from './search-input';

/**
 * A search input with a results popover anchored to it.
 *
 * Four features had re-derived this and disagreed on every part of it: how the
 * popover matches the input's width, whether focus returns to the input, and
 * what dismisses it. Those three rules live here now.
 *
 * Open state stays with the caller — on these surfaces it is derived (a term is
 * long enough, a dialog is up, a store says so), never a simple toggle, so there
 * is no trigger and `open` is always controlled.
 */

interface SearchPopoverContextValue {
  anchorRef: React.RefObject<HTMLDivElement | null>;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onOpenChange?: (open: boolean) => void;
}

const SearchPopoverContext =
  React.createContext<SearchPopoverContextValue | null>(null);

SearchPopoverContext.displayName = 'SearchPopoverContext';

const useSearchPopoverContext = (): SearchPopoverContextValue => {
  const context = React.useContext(SearchPopoverContext);
  if (!context) {
    throw new Error(
      'SearchPopoverInput and SearchPopoverContent must be used within SearchPopover',
    );
  }
  return context;
};

export interface SearchPopoverProps {
  open: boolean;
  /**
   * Dismissal. Omit it when `open` is derived from the query itself: the
   * popover then closes only when that derivation says so, and an outside click
   * or Escape leaves the results up.
   */
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

function SearchPopover({
  open,
  onOpenChange,
  children,
}: Readonly<SearchPopoverProps>) {
  const anchorRef = React.useRef<HTMLDivElement | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const value = React.useMemo<SearchPopoverContextValue>(
    () => ({ anchorRef, inputRef, onOpenChange }),
    [onOpenChange],
  );

  return (
    <SearchPopoverContext.Provider value={value}>
      {/* Routing `onOpenChange` into the Root is what makes Escape dismiss the
          results: without it Radix has nowhere to send the request. */}
      <Popover open={open} onOpenChange={onOpenChange}>
        {children}
      </Popover>
    </SearchPopoverContext.Provider>
  );
}

export interface SearchPopoverInputProps extends SearchInputProps {
  /** Classes for the anchor wrapper. The popover matches its width. */
  anchorClassName?: string;
}

function SearchPopoverInput({
  anchorClassName,
  ...inputProps
}: Readonly<SearchPopoverInputProps>) {
  const { anchorRef, inputRef } = useSearchPopoverContext();

  return (
    <PopoverAnchor asChild>
      {/* `SearchInput` forwards its ref to the inner <input>, so the anchor
          needs an element of its own to measure and to hit-test against. */}
      <div ref={anchorRef} className={anchorClassName}>
        <SearchInput ref={inputRef} {...inputProps} />
      </div>
    </PopoverAnchor>
  );
}

export type SearchPopoverContentProps = React.ComponentPropsWithoutRef<
  typeof PopoverContent
>;

function SearchPopoverContent({
  className,
  align = 'start',
  sideOffset = 4,
  onOpenAutoFocus,
  onInteractOutside,
  ...props
}: Readonly<SearchPopoverContentProps>) {
  const { anchorRef, inputRef } = useSearchPopoverContext();

  return (
    <PopoverContent
      align={align}
      sideOffset={sideOffset}
      // Radix measures the anchor into this variable, so the results track the
      // input with no ref, no ResizeObserver, and no first frame at the wrong
      // width. No minimum: the results are as wide as the input they belong to,
      // and a caller that wants a floor passes its own `min-w-*`.
      className={cn(
        'w-[var(--radix-popover-trigger-width)] max-w-[90vw] rounded-md border border-border bg-popover p-0 shadow-md',
        className,
      )}
      onOpenAutoFocus={(event) => {
        // Focus must stay in the input so typing continues uninterrupted.
        event.preventDefault();
        inputRef.current?.focus();
        onOpenAutoFocus?.(event);
      }}
      onInteractOutside={(event) => {
        // A click on the input itself is not "outside" — it would dismiss the
        // results the user just came back to. Preventing the event is also what
        // stops Radix's own dismissal; a genuine outside interaction is left to
        // fall through to it, so `onOpenChange` is called exactly once.
        if (anchorRef.current?.contains(event.target as Node)) {
          event.preventDefault();
        }
        onInteractOutside?.(event);
      }}
      {...props}
    />
  );
}

export { SearchPopover, SearchPopoverInput, SearchPopoverContent };
